/**
 * Building blocks for the shortcut schemes of other tools (plan D2, D7). A
 * scheme is a full default keymap: the Jam SQL defaults with the scheme's rows
 * laid over them. A row only exists for a command the tool really has, on the
 * platforms its documentation gives a chord for; a platform the row leaves out
 * keeps the Jam default. User overrides layer on top of the resulting scheme.
 */
import type {
  KeybindingCommandId,
  KeybindingScheme,
  Platform,
  PlatformChords,
  SchemeId,
} from '../keybindingTypes';
import { jamScheme } from './jam';

export type SchemeRow = {
  readonly commandId: KeybindingCommandId;
  readonly chords: Readonly<Partial<Record<Platform, readonly string[]>>>;
};

export type SchemeDefinition = {
  readonly scheme: KeybindingScheme;
  readonly rows: readonly SchemeRow[];
};

/** The same chords on macOS, Windows and Linux (`Mod` is ⌘ on macOS, Ctrl elsewhere). */
export const onEveryPlatform = (...chords: string[]): PlatformChords => ({ mac: chords, windows: chords, linux: chords });

/** Chords for Windows and Linux only; macOS keeps the Jam default. */
export const onWindowsAndLinux = (...chords: string[]): Partial<PlatformChords> => ({ windows: chords, linux: chords });

export function defineScheme(id: SchemeId, label: string, rows: readonly SchemeRow[]): SchemeDefinition {
  const defaults: Record<KeybindingCommandId, PlatformChords> = { ...jamScheme.defaults };
  for (const row of rows) defaults[row.commandId] = { ...defaults[row.commandId], ...row.chords };
  return { scheme: { id, label, defaults }, rows };
}

/**
 * True when the scheme gives the command other chords than the Jam default on
 * this platform — the row's chords come from the scheme, not from Jam SQL.
 */
export function isSchemeRow(scheme: KeybindingScheme, commandId: KeybindingCommandId, platform: Platform): boolean {
  const own = scheme.defaults[commandId][platform];
  const jam = jamScheme.defaults[commandId][platform];
  return own.length !== jam.length || own.some((chord, index) => chord !== jam[index]);
}
